import type {Surface} from './surface';

/**
 * The pages the shell serves itself, and the analytics view name for each.
 *
 * One list with two consumers that must not drift apart:
 *
 *   - `routeClaims.ts` reserves these paths, so no remote can claim a path the
 *     shell already renders.
 *   - `analytics/usePageViews.ts` reports a pageview under the name given here.
 *
 * The names reach analytics reports, so treat them as published: renaming one
 * breaks whatever was built on the old name.
 *
 * What this is still *not* derived from is the `<Route>` declarations in
 * `App.tsx` and `identity/IdentitySurface.tsx`. Those own the elements, this
 * owns the names, and a page added there but not here reports as not-found —
 * the safer of the two ways to be wrong.
 */
export const SHELL_PAGES: Record<Surface, Record<string, string>> = {
  forge: {
    '/': 'shell-home',
    '/login': 'shell-login',
    '/signup': 'shell-signup',
  },
  identity: {
    '/': 'identity-landing',
    '/login': 'identity-login',
    '/signup': 'identity-signup',
    '/onboarding': 'identity-onboarding',
  },
};

/**
 * Reserved, but not pages: the shell's own static files and the pod's probes.
 * No view name, because nothing routes to them in React.
 */
const RESERVED_NON_PAGE_PATHS = [
  '/shell-assets',
  '/shell-config.js',
  '/healthz',
  '/ready',
];

/**
 * Every top-level path the shell owns, on top of the remote mounts. A remote
 * claiming one of these is rejected.
 *
 * Surface-agnostic, which is how it has always behaved: `/login` is the shell's
 * whichever origin a remote tries to claim it on.
 */
export const reservedShellPaths = new Set<string>([
  ...Object.values(SHELL_PAGES).flatMap(pages => Object.keys(pages)),
  ...RESERVED_NON_PAGE_PATHS,
]);
